import * as ngCore from '@angular/core'
import * as store from '@attain-apps/fe/web/main/lib/store'
import type * as rxjsUtils from '@attain-apps/rxjs-utils'
import _cloneDeep from 'lodash-es/cloneDeep'
import _isEqual from 'lodash-es/isEqual'
import * as rxjs from 'rxjs'

import type * as associations from '../associations'
import { InjectableDependencies } from '../injectable-dependencies'
import type * as models from '../models'
import * as plugins from '../plugins'

import { getActionsConfig } from './actions'
import type { Config } from './config'
import * as effects from './effects'
import { InstanceManager } from './instance-manager'
import type { Name } from './name'
import { Selectors } from './selectors'
import { State } from './state'

// TODO: if we have entity that is primitive type string | number (somewhat primitive type),
// our entity collection wont work because we do require objects that have id property,
// or can specify which property is an id, in the long term solution whe
// should try and implement wrapping / unwrapping of the entity, so we can support primitive types as well,
// but for now we will consider that TEntity is always and object, and therefor has id property

// TODO: we might need to implement some logic when context changes if we want our effects
// to react on context changes or we just want to update context
// (e.g. we might not want to query for entities, attributes, etc. when context changes).
// If we want to react on context changes then we want to empty our state that is dependent on context
// before receiving new data (e.g. when changing scenario, data from previous scenario
// is still stored in state for a few seconds before getting new data)

/**
 * Entity collection
 *
 * TODO: better description. Should include explanation of what an entity
 * collection does, the design principle(s) behind it, when you should use it,
 * when you should _not_ use it, examples of common use cases, etc.
 */
export class Collection<
  TTypeParams extends models.TypeParams.Constraint,
  TAssociationsManager extends
    associations.Manager<TTypeParams> = associations.Manager<TTypeParams>,
> {
  /**
   * TODO:
   */
  public static FEATURE_ROOT: Readonly<store.Feature.Parent> = {
    name: 'ec',
  }

  /** @see {@link InstanceManager} */
  public static instances = new InstanceManager()

  /**
   * {@link store.Feature} for this {@link Collection}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly feature

  /** @see {@link plugins.Manager} */
  public readonly plugins: plugins.Manager<TTypeParams>

  /**
   * Indicates this entity collection has a context observable
   */
  public get usingContext(): boolean {
    return !!this._config.context$
  }

  /** @see {@link Name} */
  public get name(): Name {
    return this._config.name
  }

  /**
   * {@link models.Api} that executes requests for this {@link Collection}
   */
  public get api(): models.Api<TTypeParams> {
    return this._config.api
  }

  /**
   * @see {@link associations.Manager.Bound}
   */
  public readonly associations:
    | ReturnType<TAssociationsManager['bind']>
    | undefined

  /**
   * Emits once when the {@link Collection} is ready
   *
   * Useful with {@link rxjsUtils.delayUntil}
   *
   * @see {@link Selectors.isReady}
   * @example
   * someAction.observe().pipe(
   *   rxjsUtils.delayUntil(collection.ready$, { maxDelayed: 1 }),
   *   rxjs.map(() => ...)
   * )
   */
  public readonly ready$: rxjs.Observable<void>

  /** Prevents duplicate lifecycle teardown when Angular and custom code overlap. */
  private _destroyed = false

  constructor(
    private readonly _config: Readonly<
      Config<TTypeParams, TAssociationsManager>
    >,
    public readonly injectables: Readonly<InjectableDependencies> = ngCore.inject(
      InjectableDependencies,
    ),
  ) {
    // FIXME Currently, this causes an error to be thrown at application startup
    // because some collections with the same name are being registered multiple
    // times.
    // if (Collection.instances.get(this.name)) {
    //   throw new Error(
    //     `Entity Collection instance "${this.name}" is already registered`,
    //   )
    // }
    this.feature = new store.Feature({
      actions: getActionsConfig(this),
      initialState: State.INITIAL as State<TTypeParams>,
      injectables,
      name: `ec.${this.name}`,
      selectors: () => new Selectors(this),
    })

    // NOTE: not clear why casting is needed, but something to do with deferred
    // type parameters
    this.associations = (
      this._config.associations satisfies TAssociationsManager | undefined
    )?.bind(this) as ReturnType<TAssociationsManager['bind']> | undefined
    this.plugins = new plugins.Manager<TTypeParams>(this, _config.plugins)

    this.ready$ = this.feature.selectors.isReady.asObservable.pipe(
      rxjs.filter(isReady => isReady),
      rxjs.take(1),
      rxjs.map(() => undefined),
    )

    // Register this instance for retrieval by other instances, when needed
    Collection.instances.register(this)
    // Register the core effects
    this.feature.registerEffects(
      effects.detectContextChange(this._config.context$),
      effects.manageAssociations(this, this._config.associations),
    )

    // Initialize plugins
    this.plugins.init()

    try {
      ngCore
        .inject(ngCore.DestroyRef)
        .onDestroy(() => this.destroy('AngularInjector.Destroyed'))
    } catch {}
  }

  /** @see {@link models.Config.identify} */
  public identify(entity: TTypeParams['entity']): models.EntityId {
    return this._config.identify(entity)
  }

  /**
   * @example
   * public readonly collection = new ec.Collection(...).after(collection => {
   *   collection.feature.registerEffects(
   *     mySpecialEffect1(),
   *     mySpecialEffect2(),
   *   )
   * })
   * @deprecated No longer needed now that we typically extend Collection
   *
   * Mostly helpful for initialization logic, such as registering additional
   * effects, in contexts where that is hard to add such logic
   */
  public after(cb: (collection: this) => void): this {
    cb(this)
    return this
  }

  /**
   * Clean up the {@link Collection} instance and remove all associated plugins.
   */
  public destroy(source: string): void {
    if (this._destroyed) {
      return
    }
    this._destroyed = true

    // Destroy all features related to `this` collection since there's no
    // scenario where we'd want to keep any alive.
    this.feature.destroy(source)

    // Remove reference to this collection from the instance manager
    Collection.instances.unregister(this)
  }

  // TODO: evaluate the possibility, of having "pause" and "resume" methods.
}
